/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { Ref } from 'preact/hooks';
import { usePinch } from './usePinch';
import { getGestureZoomFactor, zoomTime } from '../utils/timeZoomUtils';
import { useUser } from '../../hooks/UNSAFE_useUser';
import { OnTimeComponentViewportChange, TimeComponentZoomParameters } from '../timeComponent.types';
import { getPositionTime } from '../utils/timeUtils';

type UsePinchZoomOptions = {
  captureTargetRef: Ref<HTMLDivElement>;
  width: number;
  startTime: number;
  endTime: number;
  viewportStartTime: number;
  viewportEndTime: number;
  majorAxisZoomOrder?: TimeComponentZoomParameters['majorAxisZoomOrder'];
  majorAxisZoomOrderIndex?: TimeComponentZoomParameters['majorAxisZoomOrderIndex'];
  minorAxisZoomOrder: TimeComponentZoomParameters['minorAxisZoomOrder'];
  minorAxisZoomOrderIndex: TimeComponentZoomParameters['minorAxisZoomOrderIndex'];
  zoomOrderViewDurationThresholds: ReturnType<
    TimeComponentZoomParameters['getZoomOrderViewDurationThresholds']
  >;
  minZoomViewportDuration: TimeComponentZoomParameters['minZoomViewportDuration'];
  maxZoomViewportDuration: TimeComponentZoomParameters['maxZoomViewportDuration'];
  onViewportChange?: OnTimeComponentViewportChange;
  isDisabled?: boolean;
};

/**
 * Hook that enables and handles pinch zoom gestures.
 */
const usePinchZoom = ({
  captureTargetRef,
  width,
  startTime,
  endTime,
  viewportStartTime,
  viewportEndTime,
  majorAxisZoomOrder,
  majorAxisZoomOrderIndex,
  minorAxisZoomOrder,
  minorAxisZoomOrderIndex,
  zoomOrderViewDurationThresholds,
  minZoomViewportDuration,
  maxZoomViewportDuration,
  onViewportChange,
  isDisabled = false
}: UsePinchZoomOptions) => {
  const { direction } = useUser();
  const isRTL = direction === 'rtl';

  const pinchProps = usePinch({
    captureTargetRef,
    onPinchZoomChange: ({ origin: { x }, delta }) => {
      const time = getPositionTime(x, viewportStartTime, viewportEndTime, width, isRTL);
      zoomTime(
        time,
        viewportStartTime,
        viewportEndTime,
        startTime,
        endTime,
        getGestureZoomFactor(delta),
        minZoomViewportDuration,
        maxZoomViewportDuration,
        zoomOrderViewDurationThresholds,
        minorAxisZoomOrder,
        minorAxisZoomOrderIndex,
        majorAxisZoomOrder,
        majorAxisZoomOrderIndex,
        onViewportChange
      );
    },
    isDisabled
  });

  return isDisabled ? {} : pinchProps;
};

export { usePinchZoom };
